import { useEffect, useMemo, useState } from "react";
import { toast, Toaster } from "sonner";
import { API } from "@/lib/api";
import { createAuthFetch, useListReorder } from "@/hooks/admin/adminApi";
import { useAdminContent } from "@/hooks/admin/useAdminContent";
import { useRegistrations } from "@/hooks/admin/useRegistrations";
import { useBatchLifecycle } from "@/hooks/admin/useBatchLifecycle";
import { useFeedbackAdmin } from "@/hooks/admin/useFeedbackAdmin";
import { useTestimonialsAdmin } from "@/hooks/admin/useTestimonialsAdmin";
import { useGuidesAdmin } from "@/hooks/admin/useGuidesAdmin";
import { AdminHeader } from "@/components/admin/AdminHeader";
import { MasterValues } from "@/components/admin/MasterValues";
import { GuideAccessPanel } from "@/components/admin/GuideAccessPanel";
import { GuideHowToAdmin } from "@/components/admin/GuideHowToAdmin";
import { OrganizerSecurityPanel } from "@/components/admin/OrganizerSecurityPanel";
import { AuditLogPanel } from "@/components/admin/AuditLogPanel";
import { LogOut } from "lucide-react";
import { AdminSidebar } from "@/components/admin/AdminSidebar";
import { CategoryRail, RAIL_STYLES, useRailStyle } from "@/components/admin/CategoryRail";
import { AdminModalsHost } from "@/components/admin/AdminModalsHost";
import { SectionCard, SectionShell } from "@/components/admin/SectionShell";
import { WelcomeMessagePanel } from "@/components/admin/content/WelcomeMessagePanel";
import { WhatsAppApiPanel } from "@/components/admin/content/WhatsAppApiPanel";
import { useClassroom } from "@/hooks/admin/useClassroom";
import { ClassroomPlayerSection, ClassroomReportsSection, ClassroomScheduleSection, ClassroomSection, ClassroomTasksSection, ClassroomVideosSection, ParticipantsTable } from "@/components/admin/ClassroomSection";
import { ZoomAttendancePanel } from "@/components/admin/zoom/ZoomAttendancePanel";
import { WaImportPanel } from "@/components/admin/whatsapp/WaImportPanel";
import { ContactsCsvPanel } from "@/components/admin/whatsapp/ContactsCsvPanel";
import { StepsViewToggle, StepsOnAlert } from "@/components/admin/StepsViewToggle";
import { MasterControl, MasterLock } from "@/components/admin/MasterControl";
import { SchoolsMasterToggle, SchoolsStages, useSchools } from "@/components/admin/SchoolsSection";
import { ChatbotSection } from "@/components/admin/ChatbotSection";
import { LayoutManager } from "@/components/admin/LayoutManager";
import { TasksTracker } from "@/components/admin/TasksAdmin";
import { TasksReportButton } from "@/components/admin/TasksReportButton";
import { BatchReportPanel } from "@/components/admin/BatchReportPanel";
import { useGuidesInfo } from "@/hooks/admin/useGuidesInfo";
import { GuidesInfoSection } from "@/components/admin/GuidesInfoSection";
import { ApprovalPendingSection, useApprovals } from "@/components/admin/ApprovalPendingSection";
import { RegistrationsHeaderBar } from "@/components/admin/RegistrationsHeaderBar";
import { AdminErrorBoundary } from "@/components/admin/AdminErrorBoundary";
import { FloatingSave } from "@/components/admin/content/FloatingSave";
import { FloatingRefresh } from "@/components/admin/content/FloatingRefresh";
import { RefreshCw } from "lucide-react";
import { SaveButton } from "@/components/admin/content/Fields";
import { SiteBasicsSection } from "@/components/admin/content/SiteBasicsSection";
import { AnnouncementSection } from "@/components/admin/content/AnnouncementSection";
import { SocialSection } from "@/components/admin/content/SocialSection";
import { FestivalSection } from "@/components/admin/content/FestivalSection";
import { DemoSection, ProgramsSection } from "@/components/admin/content/ProgramsSection";
import { FeedbackSettings, TestimonialsSettings } from "@/components/admin/content/FeedbackTestimonialSettings";
import { GuidesSettings } from "@/components/admin/content/GuidesSettings";
import { FeedbackManager } from "@/components/admin/FeedbackManager";
import { GuidesManager } from "@/components/admin/GuidesManager";
import { RegistrationsTable } from "@/components/admin/RegistrationsTable";
import { TestimonialsManager } from "@/components/admin/TestimonialsManager";
import { ExportPanel } from "@/components/admin/VisibilityExportPanels";
import { PastParticipantsPanel } from "@/components/admin/PastParticipantsPanel";

const getJson = async (path, adminKey) => {
  const res = await fetch(`${API}${path}`, { headers: { "X-PL-Client": "web", "X-Admin-Key": adminKey } });
  if (!res.ok) { const err = new Error(`${path} failed (${res.status})`); err.status = res.status; throw err; }
  return res.json();
};

/* Organizer Control Center console. Authentication happens in pages/Organizer.jsx (session cookie); `legacyKey` is only set when the
   Organizer used the emergency legacy key. Every API call is re-authorised server-side (verify_admin). */
export default function AdminConsole({ legacyKey = "", onSignOut }) {
  const [key] = useState(legacyKey);
  const [authed, setAuthed] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const authFetch = useMemo(() => createAuthFetch(key), [key]);
  const [railStyle, setRailStyle] = useRailStyle();

  const c = useAdminContent(authFetch);
  const [undoTick, setUndoTick] = useState(0);
  const afterUndo = () => { load(key); cls.reload(); setUndoTick((t) => t + 1); };
  const reg = useRegistrations(key, authFetch, afterUndo);
  const fb = useFeedbackAdmin(authFetch);
  const tm = useTestimonialsAdmin(authFetch);
  const gd = useGuidesAdmin(authFetch);
  const { dragCtx, handleDrop } = useListReorder(authFetch);
  const batch = useBatchLifecycle({ key, authFetch, rowsCount: reg.rows.length, setBatchInput: c.setBatchInput, reload: () => load(key) });
  const cls = useClassroom(authFetch, authed, () => { load(key); setUndoTick((t) => t + 1); });
  const sc = useSchools(authFetch, authed);
  const gi = useGuidesInfo(authFetch, authed);
  const ap = useApprovals(authFetch, authed, () => load(key));

  async function load(adminKey) {
    setLoading(true);
    try {
      reg.hydrate(await getJson("/admin/registrations", adminKey));
      setAuthed(true);
      const optional = async (label, fn) => {
        try { await fn(); } catch (err) { console.warn(`Admin load: ${label} failed`, err); }
      };
      await optional("settings", async () => reg.hydrateLimit((await getJson("/admin/settings", adminKey)).reg_limit));
      await optional("past participants", async () => batch.setPastTotal((await getJson("/admin/past-participants", adminKey)).total || 0));
      await optional("content", async () => c.hydrate(await getJson("/content", adminKey)));
      await optional("feedback", async () => fb.setFeedbacks(await getJson("/admin/feedback", adminKey)));
      await optional("testimonials", async () => tm.setTestimonials(await getJson("/admin/testimonials", adminKey)));
      await optional("guides", async () => gd.setGuides(await getJson("/admin/guides", adminKey)));
      await optional("guide invites", async () => gd.setGuideInvites(await getJson("/admin/guide-invites", adminKey)));
    } catch (err) {
      const msg = err?.status === 401 ? "Your Organizer session has ended. Please sign in again." : err?.status ? `Server error (${err.status}) — the backend is not responding. Please try again in a minute.` : "Cannot reach the server — check your connection or try again in a minute.";
      setLoadError(msg); toast.error(msg);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(key); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!authed) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#EDECE3] px-6" data-testid="organizer-console-loading">
        <Toaster position="top-center" richColors />
        <div className="text-center space-y-4">
          <p className="text-sm text-muted-foreground">{loadError || "Opening the Organizer Control Center…"}</p>
          {loadError && <button type="button" data-testid="organizer-console-signout" onClick={onSignOut} className="rounded-full bg-[#1A2E26] text-white px-5 py-2 text-sm">Back to sign in</button>}
        </div>
      </div>
    );
  }

  const refreshAll = async () => {
    setRefreshing(true);
    try {
      await Promise.all([load(key), ap.reload()]);
      toast.success("Refreshed — showing the latest registrations");
    } finally {
      setRefreshing(false);
    }
  };
  const refreshBtn = (
    <button type="button" data-testid="registrations-refresh-btn" onClick={refreshAll} disabled={refreshing} className="inline-flex items-center gap-2 rounded-full bg-[#1A2E26] text-white px-5 py-2.5 text-sm hover:opacity-90 transition-opacity disabled:opacity-60">
      <RefreshCw size={16} className={refreshing ? "animate-spin" : ""} /> {refreshing ? "Refreshing…" : "Refresh"}
    </button>
  );

  const { content, setC } = c;
  const contentSave = (testId) => content && <SaveButton saveContent={c.saveContent} savingContent={c.savingContent} testId={testId} />;
  const clsX = { ...cls, commence: (d) => cls.commence(d, () => load(key)), returnParticipants: (ids) => cls.returnParticipants(ids, () => load(key)), archiveParticipants: (ids) => cls.archiveParticipants(ids, batch.refreshPastTotal) };

  return (
    <AdminErrorBoundary>
    <div className="min-h-screen bg-[#EDECE3]/60 xl:pl-[calc(var(--rail-w)+216px)]" style={{ "--rail-w": `${RAIL_STYLES[railStyle].width}px` }}>
      <Toaster position="top-center" richColors />
      <CategoryRail style={railStyle} setStyle={setRailStyle} />
      <AdminSidebar />
      <button type="button" data-testid="organizer-signout-btn" onClick={onSignOut} title="Sign out of the Organizer Control Center" className="fixed top-3 right-3 z-50 inline-flex items-center gap-1.5 rounded-full border border-[#1A2E26]/15 bg-white/90 backdrop-blur px-3 py-1.5 text-xs shadow hover:bg-[#1A2E26] hover:text-white transition-colors"><LogOut size={12} /> Sign out{legacyKey ? " (legacy key)" : ""}</button>
      <div className="mx-auto max-w-6xl px-6 py-12">
        <SectionShell id="batch" description="Master values for Future / Present / School batches, seats, archive and quick actions">
          <AdminHeader
            rows={reg.rows}
            regLimit={reg.regLimit}
            batchInput={c.batchInput}
            setBatchInput={c.setBatchInput}
            saveBatch={c.saveBatch}
            savingBatch={c.savingBatch}
            startNewBatch={batch.startNewBatch}
            startingBatch={batch.startingBatch}
            archiveBatch={batch.archiveBatch}
            archiving={batch.archiving}
            uploadPast={batch.uploadPast}
            pastTotal={batch.pastTotal}
            limitInput={reg.limitInput}
            setLimitInput={reg.setLimitInput}
            saveLimit={reg.saveLimit}
            savingLimit={reg.savingLimit}
            fewPctInput={c.fewPctInput}
            setFewPctInput={c.setFewPctInput}
            saveFewPct={c.saveFewPct}
            savingFewPct={c.savingFewPct}
            selected={reg.selected}
              deleteSelected={reg.deleteSelected}
            onRefresh={() => load(key)}
            onAddRegistration={() => reg.openAddRegistration(content?.batch_no)}
          />
          <MasterValues authFetch={authFetch} cls={cls} refreshKey={`${content?.batch_no}|${JSON.stringify(sc.counts || {})}`} onChanged={() => { load(key); sc.load(); }} />
        </SectionShell>


        {content && (
          <>
            <SectionShell id="site" description="Title, gratitude line, hero image, footer and navigation tabs" action={contentSave("content-save-btn")}>
              <SiteBasicsSection content={content} setC={setC} />
            </SectionShell>
            <SectionShell id="announcement" description="Banner, scrolling notice, Zoom link, batch dates, sharings and program details" action={contentSave("content-save-btn-announcement")}>
              <AnnouncementSection content={content} setC={setC} annUploading={c.annUploading} uploadAnnImage={c.uploadAnnImage} />
            </SectionShell>
            <SectionShell id="social" description="Website, YouTube, Facebook, Instagram and Email shown on the main page" action={contentSave("content-save-btn-social")}>
              <SocialSection content={content} setC={setC} />
            </SectionShell>
            <SectionShell id="festival" description="Idol image, wish text and falling petals" action={contentSave("content-save-btn-festival")}>
              <FestivalSection content={content} setC={setC} />
            </SectionShell>
            <SectionShell id="programs" description="Program cards, capacity, register links and the pre-registration checklist" action={contentSave("content-save-btn-programs")}>
              <ProgramsSection
                content={content}
                setC={setC}
                authFetch={authFetch}
                setContent={c.setContent}
                setProg={c.setProg}
                addProgram={c.addProgram}
                removeProgram={c.removeProgram}
                imgUploading={c.imgUploading}
                uploadProgramImage={c.uploadProgramImage}
              />
            </SectionShell>
            <SectionShell id="demo" description="Participant how-to demos — the 'How to open the video' guide beside Register and all How-To guides (Register, Zoom, Breakout Rooms, Daily Tasks, Audio…) with formats, device views and visibility" action={contentSave("content-save-btn-demo")}>
              <DemoSection content={content} setC={setC} authFetch={authFetch} />
            </SectionShell>
          </>
        )}

        <SectionShell id="feedback" description="Feedback settings, alerts and moderation" action={contentSave("content-save-btn-feedback")}>
          {content && (
            <FeedbackSettings content={content} setC={setC} toggleContentFlag={c.toggleContentFlag} sendTestFeedbackAlert={c.sendTestFeedbackAlert} sendingTestAlert={c.sendingTestAlert} />
          )}
          <FeedbackManager
            feedbacks={fb.feedbacks}
            approveFeedback={fb.approveFeedback}
            toggleFeedbackVisible={fb.toggleFeedbackVisible}
            startEditFb={fb.startEditFb}
            deleteFeedback={fb.deleteFeedback}
          />
        </SectionShell>

        <SectionShell id="testimonials" description="Visibility and testimonial entries">
          {content && <TestimonialsSettings content={content} toggleContentFlag={c.toggleContentFlag} />}
          <TestimonialsManager {...tm} handleDrop={handleDrop} dragCtx={dragCtx} />
        </SectionShell>

        <SectionShell id="guides" description="Guides page settings, invites and guide entries" action={contentSave("content-save-btn-guides")}>
          {content && <GuidesSettings content={content} setC={setC} toggleContentFlag={c.toggleContentFlag} />}
          <GuidesManager {...gd} handleDrop={handleDrop} dragCtx={dragCtx} />
        </SectionShell>

        <SectionShell id="past" description="Participants archived from earlier batches — select some or all and send a new-batch announcement on WhatsApp">
          <PastParticipantsPanel adminKey={key} onChanged={batch.refreshPastTotal} content={content} setC={setC} onContentSaved={c.syncContent} />
        </SectionShell>

        {content && (
          <SectionShell id="welcome" description="Welcome template sent to WhatsApp and email as soon as someone registers" action={contentSave("content-save-btn-welcome")}>
            <SectionCard id="welcome" testId="section-welcome">
              <WhatsAppApiPanel content={content} setC={setC} adminKey={key} onContentSaved={c.syncContent} />
              <WelcomeMessagePanel content={content} setC={setC} />
            </SectionCard>
          </SectionShell>
        )}

        <SectionShell id="classroom" description="Commence the batch, Register Steps look, Today's Session and login statistics" titleAside={<MasterControl cls={cls} />}>
          <MasterLock cls={cls}>
            <ClassroomSection cls={clsX} />
          </MasterLock>
        </SectionShell>
        <SectionShell id="classroom-main" description="Day 1 date, play times and option for Participants, Guides and Schools; Step 2 / Step 3 passwords and Open Video authentication inside Participants">
          <MasterLock cls={cls}><ClassroomScheduleSection cls={clsX} content={content} sc={sc} /></MasterLock>
        </SectionShell>
        <SectionShell id="classroom-tasks" action={<TasksReportButton authFetch={authFetch} />} description="Task list, test day and who updates the status — Participants themselves or Guides">
          <MasterLock cls={cls}><ClassroomTasksSection cls={clsX} /></MasterLock>
        </SectionShell>
        <SectionShell id="classroom-videos" description="Video libraries — Classroom Videos (Day 1–6), Classroom Videos (Day 1–10) and Schools videos">
          <MasterLock cls={cls}><ClassroomVideosSection cls={clsX} sc={sc} /></MasterLock>
        </SectionShell>
        <SectionShell id="classroom-reports" description="Daily Reports and Watch-time insights for the running batch">
          <MasterLock cls={cls}><ClassroomReportsSection cls={clsX} authFetch={authFetch} /></MasterLock>
        </SectionShell>
        <SectionShell id="classroom-player" description="Video Player look and the Classroom screen texts & blocks participants see">
          <MasterLock cls={cls}><ClassroomPlayerSection cls={clsX} authFetch={authFetch} /></MasterLock>
        </SectionShell>
        <SectionShell id="zoom" description="Zoom attendance — join/leave, minutes and attendance % per session; auto-ticks Daily Mandatory Tasks; reports, exports, access and retention">
          <SectionCard id="zoom" testId="section-zoom"><ZoomAttendancePanel authFetch={authFetch} /></SectionCard>
        </SectionShell>
        <SectionShell id="wa-hub" description="WhatsApp group tools — import chat exports to record audio/video sharings per participant, with reports and CSV/JSON export">
          <SectionCard id="wa-hub" testId="section-wa-hub">
            <WaImportPanel authFetch={authFetch} />
            <ContactsCsvPanel authFetch={authFetch} />
            <div className="h-16" />
          </SectionCard>
        </SectionShell>

        <SectionShell id="present" description="Participants of the running batch — add, edit, move back to Future Batch or empty to Past Batch" action={<StepsViewToggle cls={cls} who="present" />} warn={(cls.classroom?.home_audience || ["guides", "present"]).includes("present")} alert={<StepsOnAlert cls={cls} who="present" />}>
          <SectionCard id="present" testId="section-present">
            {cls.batch?.status === "running" ? <><ParticipantsTable batch={cls.batch} content={content} cls={{ ...cls, commence: (d) => cls.commence(d, reg.selected.length ? reg.selected : null, () => { load(key); setUndoTick((t) => t + 1); }) }} /><TasksTracker cls={cls} /></> : <p className="text-sm text-muted-foreground" data-testid="present-no-batch">No batch is running. Commence a batch from Classroom (13a) or Future Batch.</p>}
          </SectionCard>
        </SectionShell>
        <SectionShell id="batch-report" description="One-page picture of the batch in progress or about to start — settings, strength, attendance, mandatory tasks, who is stopped — with WhatsApp delivery">
          <SectionCard id="batch-report" testId="section-batch-report">
            <BatchReportPanel cls={cls} />
          </SectionCard>
        </SectionShell>
        <SectionShell id="guide-info" action={<StepsViewToggle cls={cls} who="guides" />} description="Guides Information link, who has filled it in, and Guide Access Management for the Guide Workspace (/guide)">
          <GuidesInfoSection gi={gi} batchNo={content?.batch_no} questions={cls.classroom?.checkin_questions || []} />
          <GuideAccessPanel authFetch={authFetch} refreshKey={gi.guides.length} />
          <GuideHowToAdmin authFetch={authFetch} />
        </SectionShell>

        <SectionShell id="approval" description="Repeat participants waiting for your approval — tick and approve selected, one by one, or all at once">
          <ApprovalPendingSection ap={ap} />
        </SectionShell>

        <SectionShell id="export" description="Download registrations as CSV / Excel; archive or delete the selected records">
          <ExportPanel
            exportCols={reg.exportCols}
            toggleCol={reg.toggleCol}
            exportSelectedOnly={reg.exportSelectedOnly}
            setExportSelectedOnly={reg.setExportSelectedOnly}
            selectedRows={reg.rows.filter((r) => reg.selected.includes(r.id))}
            runExport={reg.runExport}
            archiveSelected={() => reg.archiveSelected(batch.refreshPastTotal)}
            archiving={reg.archiving}
            copyWaNames={reg.copyWaNames}

          />
        </SectionShell>

        <SectionShell id="registrations" description="Participants registered for the upcoming batch — select records and move them to the Present Batch" action={refreshBtn}>
          <RegistrationsHeaderBar batchNo={content?.batch_no} selectedIds={reg.selected} cls={cls} onMoved={() => { load(key); setUndoTick((t) => t + 1); }} onAdd={() => reg.openAddRegistration(content?.batch_no)} onUpload={(e) => reg.uploadCsv(e, () => { load(key); setUndoTick((t) => t + 1); })} uploading={reg.uploadingRegs} />
          <RegistrationsTable
            rows={reg.rows}
            visibleRows={reg.visibleRows}
            agreedFilter={reg.agreedFilter}
            setAgreedFilter={reg.setAgreedFilter}
            selected={reg.selected}
            toggleAll={reg.toggleAll}
            toggleSelect={reg.toggleSelect}
            content={content}
            adminKey={key}
            authFetch={authFetch}
            onSent={async () => { try { reg.hydrate(await getJson("/admin/registrations", key)); } catch {} }}
            startEdit={reg.startEdit}
            deleteOne={reg.deleteOne}
          />
        </SectionShell>
        <SectionShell id="schools" description="Independent Schools pipeline — Approval Pending → Future Batch → Present Batch → Past Batch. Switch OFF to hide it from the website." action={<SchoolsMasterToggle sc={sc} />}>
          <SchoolsStages sc={sc} authFetch={authFetch} />
        </SectionShell>
        <SectionShell id="chatbot" description="AI chat widget on every page — answers only from your knowledge base and live website facts, captures name & email, and hands off to the team on WhatsApp">
          <SectionCard id="chatbot" testId="section-chatbot">
            <ChatbotSection authFetch={authFetch} />
          </SectionCard>
        </SectionShell>
        <SectionShell id="layout" description="Arrange the participant page — drag blocks between columns, reorder, show/hide, then Save draft → Preview website → Publish">
          <SectionCard id="layout" testId="section-layout">
            <LayoutManager authFetch={authFetch} />
          </SectionCard>
        </SectionShell>
        <SectionShell id="security" description="Organizer two-step sign-in, legacy key, sessions and the activity log of every Organizer and Guide action">
          <SectionCard id="security" testId="section-security">
            <OrganizerSecurityPanel authFetch={authFetch} usingLegacyKey={!!legacyKey} onSignOut={onSignOut} />
            <AuditLogPanel authFetch={authFetch} />
          </SectionCard>
        </SectionShell>

      </div>

      {content && <FloatingSave content={content} savedVersion={c.savedVersion} saveContent={c.saveContent} savingContent={c.savingContent} />}
      <FloatingRefresh onRefresh={refreshAll} refreshing={refreshing} />
      <AdminModalsHost reg={reg} fb={fb} tm={tm} gd={gd} />
    </div>
    </AdminErrorBoundary>
  );
}
